import { HistoryService } from './HistoryService';
import { HistoryActionTypes } from '../../enums';
import { IAuditoriumHistoryState } from '../../interfaces';
import { CreatorActionsService } from './CreatorActionsService';
import { BaseElementManager, ImageManager, SeatManager, ShapeManager } from '../../managers';
import { cloneDeep } from 'lodash';
import { forkJoin, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CreatorStateService } from './CreatorStateService';

export class CreatorHistoryService {

  public constructor(
    private readonly _historyService: HistoryService<IAuditoriumHistoryState>
  ) {
  }

  public undo(): Observable<{
    current: IAuditoriumHistoryState,
    previous: IAuditoriumHistoryState
  }> {
    return this.undoRedoActions(this._historyService.undo(), HistoryActionTypes.UNDO);
  }

  public redo(): Observable<{
    current: IAuditoriumHistoryState,
    previous: IAuditoriumHistoryState
  }> {
    return this.undoRedoActions(this._historyService.redo(), HistoryActionTypes.REDO);
  }

  protected undoRedoActions(
    action: { current: IAuditoriumHistoryState, previous: IAuditoriumHistoryState },
    type: HistoryActionTypes
  ): Observable<{ current: IAuditoriumHistoryState, previous: IAuditoriumHistoryState }> {
    if (!action) {
      return of({
        current: null,
        previous: null
      });
    }

    const currentSeats = cloneDeep(action.current?.seats) ?? null;
    const currentBaseElements = cloneDeep(action.current?.baseElements) ?? null;
    const currentSectors = cloneDeep(action.current?.sectors) ?? null;
    const currentImages = cloneDeep(action.current?.images) ?? null;
    const currentPriorityShape = cloneDeep(action.current?.priorityShape) ?? null;

    const previousSeats = cloneDeep(action.previous?.seats) ?? null;
    const previousBaseElements = cloneDeep(action.previous?.baseElements) ?? null;
    const previousSectors = cloneDeep(action.previous?.sectors) ?? null;
    const previousImages = cloneDeep(action.previous?.images) ?? null;
    const previousPriorityShape = cloneDeep(action.previous?.priorityShape) ?? null;

    const subs$: Observable<HTMLElement>[] = [];

    if (currentSeats && currentSeats?.length) {
      if (previousSeats && previousSeats?.length) {
        const noPrevStateSeats = currentSeats.filter(x => !previousSeats.some(y => y.guid === x.guid));
        const noCurrentStateSeats = previousSeats.filter(x => !currentSeats.some(y => y.guid === x.guid));
        const seatsToUpdate = previousSeats.filter(x => currentSeats.some(y => y.guid === x.guid));

        const updatedSeats = CreatorActionsService.updateSeats(seatsToUpdate);

        for (const uSeat of updatedSeats) {
          subs$.push(
            ShapeManager.renewElement$(
              SeatManager.getHTMLElement(uSeat.guid),
              uSeat,
              CreatorStateService.currentStep === 'connections'
                ? {position: 'absolute', enableHover: true, shouldCreateResizePoints: false, shapeId: uSeat.sectorElement.shapeId}
                : {position: 'absolute', showContextMenu: true, shapeId: uSeat.sectorElement.shapeId}
            )
          );
        }

        if (noPrevStateSeats?.length) {
          CreatorActionsService.deleteSeats(noPrevStateSeats);
        }

        if (noCurrentStateSeats?.length) {
          CreatorActionsService.addSeats(noCurrentStateSeats, {emitEvent: true});
        }
      } else {
        CreatorActionsService.deleteSeats(currentSeats);
      }
    } else if (previousSeats && previousSeats?.length) {
      CreatorActionsService.addSeats(previousSeats, {emitEvent: true});
    }

    if (currentPriorityShape) {
      if (previousPriorityShape) {
        const updatedPriorityShape = CreatorActionsService.changePriorityShape(previousPriorityShape);
        subs$.push(ShapeManager.updatePriorityShape$(updatedPriorityShape));
      }
    }

    if (currentImages && currentImages?.length) {
      if (previousImages && previousImages?.length) {
        const updatedImages = CreatorActionsService.updateImages(previousImages);

        for (const uImage of updatedImages) {
          subs$.push(ShapeManager.renewImageElement$(ImageManager.getHTMLElement(uImage.guid), uImage));
        }
      } else {
        CreatorActionsService.deleteImages(currentImages);
      }
    } else if (previousImages && previousImages?.length) {
      CreatorActionsService.addImages(previousImages, {emitEvent: true});
    }

    if (currentBaseElements && currentBaseElements?.length) {
      if (previousBaseElements && previousBaseElements?.length) {
        const updatedBaseElements = CreatorActionsService.updateBaseElements(previousBaseElements);

        for (const uBaseElement of updatedBaseElements) {
          subs$.push(
            ShapeManager.renewElement$(
              BaseElementManager.getHTMLElement(uBaseElement.guid),
              uBaseElement,
              CreatorStateService.currentStep === 'connections'
                ? {position: 'absolute', enableHover: true, shouldCreateResizePoints: false}
                : {position: 'absolute', showContextMenu: true}
            )
          );
        }
      } else {
        CreatorActionsService.deleteBaseElements(currentBaseElements);
      }
    } else if (previousBaseElements && previousBaseElements?.length) {
      CreatorActionsService.addBaseElements(previousBaseElements, {emitEvent: true});
    }

    if (currentSectors && currentSectors?.length) {
      if (previousSectors && previousSectors?.length) {
        const updatedSectors = CreatorActionsService.updateSectors(previousSectors);

        for (const uSector of updatedSectors) {
          subs$.push(
            ShapeManager.renewElement$(
              BaseElementManager.getHTMLElement(uSector.guid),
              uSector,
              CreatorStateService.currentStep === 'connections'
                ? {position: 'absolute', enableHover: true, shouldCreateResizePoints: false}
                : {position: 'absolute', showContextMenu: true}
            )
          );
        }
      } else {
        CreatorActionsService.deleteSectors(currentSectors);
      }
    } else if (previousSectors && previousSectors?.length) {
      CreatorActionsService.addSectors(previousSectors, {emitEvent: true});
    }

    return forkJoin(subs$.length ? subs$ : [of([])]).pipe(
      tap(() => {
        switch (type) {
          case HistoryActionTypes.UNDO:
            this._historyService.addToRevertedHistory(
              {
                seats: previousSeats,
                baseElements: previousBaseElements,
                sectors: previousSectors,
                images: previousImages,
                priorityShape: previousPriorityShape
              },
              {
                seats: currentSeats,
                baseElements: currentBaseElements,
                sectors: currentSectors,
                images: currentImages,
                priorityShape: currentPriorityShape
              }
            );
            break;
          case HistoryActionTypes.REDO:
            this._historyService.addToHistory(
              {
                seats: previousSeats,
                baseElements: previousBaseElements,
                sectors: previousSectors,
                images: previousImages,
                priorityShape: previousPriorityShape
              },
              {
                seats: currentSeats,
                baseElements: currentBaseElements,
                sectors: currentSectors,
                images: currentImages,
                priorityShape: currentPriorityShape
              },
              false
            );
            break;
        }
      }),
      map(() => ({
        current: {
          seats: currentSeats,
          baseElements: currentBaseElements,
          sectors: currentSectors,
          images: currentImages,
          priorityShape: currentPriorityShape
        },
        previous: {
          seats: previousSeats,
          baseElements: previousBaseElements,
          sectors: previousSectors,
          images: previousImages,
          priorityShape: previousPriorityShape
        }
      }))
    );
  }
}
